import { DeleteOperationItemNotFoundError } from "@tanstack/query-db-collection";
import { DuplicateKeyError, useLiveQuery, eq } from "@tanstack/react-db";
import { useEffect } from "react";
import {
  documentTypesCollection,
  userTagsCollection,
  conversationsCollection,
  conversationContentCollection,
  conversationQuestionsCollection,
  userPreferencesCollection,
  viewportCollection,
  defaultUserTagsCollection,
} from "@/tanstack-db/collections";
import type {
  Tag,
  Conversation,
  ConversationContentRow,
  UserPreferences,
} from "@/tanstack-db/schemas";
import type { ConversationQuestion, TagId } from "@/types";
import {
  API_ENDPOINTS,
  NO_ACTIVE_CONVERSATION_QUERY_KEY,
  SESSION_SCOPED_USER_QUERY_KEY,
} from "@/constants";
import {
  isAssistDisplayBlocked,
  subscribeAssistDisplayGeometry,
} from "@/lib/ui-helper/assist-display";
import { deleteConversationContent } from "@/lib/queries/conversation-content";
import { fetchWithAuthRedirect } from "@/lib/api/auth-redirect";
import { queryClient } from "@/tanstack-db/query-client";

// ─────────────────────────────────────────────────────────────────────────────
// Document Types Hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get document types (reference data)
 */
export function useDocumentTypes() {
  const { data: documentTypes = [] } = useLiveQuery({
    queryKey: ["live", "document-types"],
    query: (q) => q.from({ dt: documentTypesCollection }),
  });
  return documentTypes as unknown as Tag[];
}

// ─────────────────────────────────────────────────────────────────────────────
// User Tags Hooks (with mutations)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get user tags with mutations
 */
export function useUserTags() {
  const { data: userTags = [] } = useLiveQuery({
    queryKey: ["live", "user-tags"],
    query: (q) => q.from({ tag: userTagsCollection }),
  });
  const createTag = (tag: Tag) => {
    userTagsCollection.insert(tag);
  };

  const deleteTag = (tagId: string) => {
    userTagsCollection.delete(tagId as TagId);
    // Optimistic: remove this tag from every conversation's customTags so the list clears immediately
    const conversations = queryClient.getQueryData<Conversation[]>([
      "conversations",
      SESSION_SCOPED_USER_QUERY_KEY,
    ]);
    if (Array.isArray(conversations)) {
      for (const conv of conversations) {
        if (conv.customTags?.includes(tagId)) {
          conversationsCollection.update(conv.id, (draft) => {
            draft.customTags = draft?.customTags?.filter((t) => t !== tagId);
          });
        }
      }
    }
  };

  const updateTagColor = (tagId: string, color: string) => {
    userTagsCollection.update(tagId as TagId, (draft) => {
      draft.color = color;
    });
  };

  return { userTags: userTags as Tag[], createTag, deleteTag, updateTagColor };
}

// ─────────────────────────────────────────────────────────────────────────────
// Conversations Hooks
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get all conversations with optional filtering
 */
export function useConversations(filters?: {
  searchQuery?: string;
  tagIds?: string[];
  sortOrder?: "asc" | "desc";
}) {
  const sortOrder = filters?.sortOrder ?? "desc";
  const { data: conversations = [] } = useLiveQuery({
    queryKey: ["live", "conversations", sortOrder],
    query: (q) =>
      q
        .from({ conv: conversationsCollection })
        .orderBy(({ conv }) => conv.lastActivityAt, sortOrder),
  });

  // Client-side filtering for search and tags (more flexible)
  let filtered = conversations;

  if (filters?.searchQuery) {
    const query = filters.searchQuery.toLowerCase();
    filtered = filtered.filter((c) => c.title.toLowerCase().includes(query));
  }

  if (filters?.tagIds && filters.tagIds.length > 0) {
    filtered = filtered.filter((c) =>
      filters.tagIds!.some(
        (tagId) =>
          c.documentTypes.includes(tagId) || c.customTags.includes(tagId)
      )
    );
  }

  return filtered;
}

/**
 * Live row from conversationContentCollection (transcript + CoT snapshot).
 */
export function useConversationContentRow(
  conversationId: string | undefined
): ConversationContentRow | null {
  const conversationKey = conversationId ?? NO_ACTIVE_CONVERSATION_QUERY_KEY;
  const { data: rows = [] } = useLiveQuery({
    queryKey: ["live", "conversation-content", conversationKey],
    query: (q) =>
      q
        .from({ row: conversationContentCollection })
        .where(({ row }) => eq(row.conversationId, conversationKey)),
  });
  return rows[0] ?? null;
}

/**
 * Get all conversation questions (global collection, SWR).
 * Returns a Map conversationId -> questions for use in ConversationList.
 */

export function useConversationQuestionsMap(): Map<
  string,
  ConversationQuestion[]
> {
  const { data: items = [] } = useLiveQuery({
    queryKey: ["live", "conversation-questions"],
    query: (q) => q.from({ cq: conversationQuestionsCollection }),
  });
  const map = new Map<string, ConversationQuestion[]>();
  for (const item of items) {
    map.set(item.conversationId, item.questions);
  }
  return map;
}

/**
 * Conversation mutations
 */
export function useConversationMutations() {
  const createConversation = (data: Omit<Conversation, "dbId">) => {
    conversationsCollection.insert(data);
  };

  const updateConversation = (id: string, updates: Partial<Conversation>) => {
    conversationsCollection.update(id, (draft) => {
      Object.assign(draft, updates);
    });
  };

  const deleteConversation = (id: string) => {
    deleteConversationContent(id);
    try {
      conversationQuestionsCollection.utils.writeDelete(id);
    } catch (error) {
      if (!(error instanceof DeleteOperationItemNotFoundError)) throw error;
    }
    conversationsCollection.delete(id);
  };

  const updateConversationTags = async (
    conversationId: string,
    tagIds: string[],
    type: "documentTypes" | "customTags"
  ) => {
    // Get original values BEFORE optimistic update
    const originalConv = conversationsCollection.get(conversationId);
    if (!originalConv?.dbId) return;

    const originalTags =
      type === "documentTypes"
        ? originalConv.documentTypes
        : originalConv.customTags;
    const addedTags = tagIds.filter((t) => !originalTags.includes(t));
    const removedTags = originalTags.filter((t) => !tagIds.includes(t));

    // Optimistic update
    conversationsCollection.update(conversationId, (draft) => {
      draft[type] = tagIds;
    });

    // Sync labels to backend (handled separately due to junction table)
    const conv = conversationsCollection.get(conversationId);
    if (!conv?.dbId) return;

    const labelType = type === "documentTypes" ? "documentType" : "userTag";
    const API_BASE_URL = "";

    // Sync labels to backend
    const results = await Promise.allSettled([
      ...addedTags.map((tagId) =>
        fetchWithAuthRedirect(
          `${API_BASE_URL}${API_ENDPOINTS.CONVERSATION_LABELS(conv.dbId!)}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ tagId, labelType }),
          }
        )
      ),
      ...removedTags.map((tagId) =>
        fetchWithAuthRedirect(
          `${API_BASE_URL}${API_ENDPOINTS.CONVERSATION_LABEL(conv.dbId!, labelType, tagId)}`,
          { method: "DELETE" }
        )
      ),
    ]);

    // Check for failures and revert optimistic update if needed
    const hasFailures = results.some((result) => result.status === "rejected");
    if (hasFailures) {
      // Revert optimistic update on failure
      conversationsCollection.update(conversationId, (draft) => {
        draft[type] = originalTags;
      });
      throw new Error("Failed to update conversation tags");
    }

    // No invalidation needed - optimistic update already applied
    // Collection will stay in sync with backend
  };

  return {
    createConversation,
    updateConversation,
    deleteConversation,
    updateConversationTags,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// User Preferences Hook (with cross-tab sync)
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_COLLAPSE_GROUPS: string[] = [];
const EMPTY_COLLAPSE_LEAVES: string[] = [];
const DEFAULT_CONVERSATION_LIST_COLLAPSE = {
  groups: EMPTY_COLLAPSE_GROUPS,
  leaves: EMPTY_COLLAPSE_LEAVES,
};

const DEFAULT_USER_PREFERENCES: UserPreferences = {
  key: "user-preferences",
  currentConversationId: null,
  showChainOfThought: false,
  theme: "light",
  sidebarWidth: 250,
  suggestionResponseVisible: {},
  suggestionResponseGateDismissed: false,
  conversationListCollapse: DEFAULT_CONVERSATION_LIST_COLLAPSE,
};

export function useUserPreferences() {
  const { data: preferences = [] } = useLiveQuery({
    queryKey: ["live", "user-preferences"],
    query: (q) => q.from({ pref: userPreferencesCollection }),
  });

  const prefs = preferences[0] ?? DEFAULT_USER_PREFERENCES;

  const updatePreferences = (
    updates: Partial<Omit<UserPreferences, "key">>
  ) => {
    // Ensure preferences exist before updating
    const existing = userPreferencesCollection.get("user-preferences");
    if (existing) {
      userPreferencesCollection.update("user-preferences", (draft) => {
        Object.assign(draft, updates);
      });
      return;
    }
    try {
      userPreferencesCollection.insert({
        key: "user-preferences" as const,
        currentConversationId: null,
        showChainOfThought: false,
        theme: "light" as const,
        sidebarWidth: 250,
        suggestionResponseVisible: {},
        suggestionResponseGateDismissed: false,
        conversationListCollapse: DEFAULT_CONVERSATION_LIST_COLLAPSE,
        ...updates,
      });
    } catch (error) {
      // Seed/hydration or Strict Mode can race past get() after react-db 0.5.
      if (!(error instanceof DuplicateKeyError)) throw error;
      userPreferencesCollection.update("user-preferences", (draft) => {
        Object.assign(draft, updates);
      });
    }
  };

  const setSuggestionResponseVisible = (
    conversationId: string,
    messageId: string,
    visible: boolean
  ) => {
    const prev = prefs.suggestionResponseVisible ?? {};
    const conv = { ...(prev[conversationId] ?? {}) };
    if (visible) {
      conv[messageId] = true;
    } else {
      delete conv[messageId];
    }
    const next = { ...prev };
    if (Object.keys(conv).length > 0) {
      next[conversationId] = conv;
    } else {
      delete next[conversationId];
    }
    updatePreferences({ suggestionResponseVisible: next });
  };

  const setSuggestionResponseGateDismissed = (dismissed: boolean) => {
    updatePreferences({ suggestionResponseGateDismissed: dismissed });
  };

  /**
   * Reveal one suggestion and optionally dismiss the gate in a single write.
   * Two writes in the same tick would insert then update the localStorage row,
   * which breaks the live query graph ("row key are not congruent").
   */
  const confirmSuggestionResponse = (
    conversationId: string,
    messageId: string,
    options?: { dismissGlobally?: boolean }
  ) => {
    const prev = prefs.suggestionResponseVisible ?? {};
    const conv = { ...(prev[conversationId] ?? {}), [messageId]: true };
    const next = { ...prev, [conversationId]: conv };

    updatePreferences({
      suggestionResponseVisible: next,
      ...(options?.dismissGlobally
        ? { suggestionResponseGateDismissed: true }
        : {}),
    });
  };

  const setConversationListCollapse = (collapse: {
    groups: string[];
    leaves: string[];
  }) => {
    updatePreferences({ conversationListCollapse: collapse });
  };

  // Sync theme to document class when it changes
  useEffect(() => {
    const root = document.documentElement;
    if (prefs.theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, [prefs.theme]);

  return {
    ...prefs,
    setCurrentConversationId: (id: string | null) =>
      updatePreferences({ currentConversationId: id }),
    setShowChainOfThought: (show: boolean) =>
      updatePreferences({ showChainOfThought: show }),
    setTheme: (theme: "light" | "dark") => updatePreferences({ theme }),
    toggleTheme: () =>
      updatePreferences({ theme: prefs.theme === "light" ? "dark" : "light" }),
    setSidebarWidth: (width: number) =>
      updatePreferences({ sidebarWidth: width }),
    setSuggestionResponseVisible,
    setSuggestionResponseGateDismissed,
    confirmSuggestionResponse,
    setConversationListCollapse,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Viewport Hook (UI State)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Sync display-block state (declared mobile or physical short side under floor).
 * Call this ONCE in DatabaseProvider
 */
export function useViewportSync() {
  useEffect(() => {
    const update = () => {
      const isMobile = isAssistDisplayBlocked();
      const existing = viewportCollection.get("viewport");
      if (existing) {
        viewportCollection.update("viewport", (draft) => {
          draft.isMobile = isMobile;
        });
        return;
      }
      try {
        viewportCollection.insert({
          key: "viewport" as const,
          isMobile,
        });
      } catch (error) {
        // Seed/hydration or Strict Mode can race past get() after react-db 0.5.
        if (!(error instanceof DuplicateKeyError)) throw error;
        viewportCollection.update("viewport", (draft) => {
          draft.isMobile = isMobile;
        });
      }
    };
    update();
    return subscribeAssistDisplayGeometry(update);
  }, []);
}

// ─────────────────────────────────────────────────────────────────────────────
// Default User Tags Hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get default user tag IDs (reference data)
 */
export function useDefaultTagIds(): Set<string> {
  const { data: tags = [] } = useLiveQuery({
    queryKey: ["live", "default-user-tags"],
    query: (q) => q.from({ tag: defaultUserTagsCollection }),
  });
  return new Set(tags.map((t) => t.tagId));
}
